import React, { useState, useMemo, useEffect } from "react";
import {
  Plus,
  Search,
  Eye,
  Pencil,
  Trash2,
  X,
  ChevronDown,
  ChevronRight,
  Layers,
  Award,
  ShieldCheck,
  Wallet,
  HeartPulse,
  FileSpreadsheet,
  Lock,
  Building2,
} from "lucide-react";
import useEmployeeMasters from "../../hooks/useEmployeeMasters";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate } from "../../utils/dateUtils";

function Card({ className = "", children }) {
  return (
    <div
      className={`bg-white rounded-2xl shadow-sm border border-gray-200 ${className}`}
    >
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const isActive = status === "active";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${isActive
        ? "bg-green-50 text-green-700 ring-1 ring-inset ring-green-200"
        : "bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200"
        }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-green-500" : "bg-gray-400"
          }`}
      />
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

function IconButton({ onClick, label, children, tone = "default" }) {
  const toneClasses =
    tone === "danger"
      ? "text-gray-500 hover:text-red-600 hover:bg-red-50"
      : "text-gray-500 hover:text-blue-600 hover:bg-blue-50";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`p-1.5 rounded-lg transition-colors ${toneClasses}`}
    >
      {children}
    </button>
  );
}

function PrimaryButton({ onClick, children, className = "", type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${className}`}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ onClick, children, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 ${className}`}
    >
      {children}
    </button>
  );
}

function EmptyState({ title, subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6">
      <div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center mb-3">
        <FileSpreadsheet className="h-6 w-6 text-blue-500" />
      </div>
      <p className="text-sm font-medium text-gray-700">{title}</p>
      <p className="text-sm text-gray-400 mt-1">{subtitle}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputClasses =
  "w-full rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors";

/* ==========================================================================
   DIALOG SHELL
   ========================================================================== */

function Dialog({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-gray-100 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close dialog"
          >
            <X className="h-4.5 w-4.5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/60 rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   VIEW DESIGNATION DIALOG
   ========================================================================== */

function ViewDesignationDialog({
  open,
  onClose,
  designationData,
  selectedDepartment,
  selectedType,
  designationTypes,
  departments,
  selectedDepartmentId,
}) {
  if (!designationData) return null;

  const typeLabel = designationTypes?.find(t => t.id === selectedType)?.label || selectedType;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="View Designation"
      footer={<SecondaryButton onClick={onClose}>Close</SecondaryButton>}
    >
      <div className="space-y-4">
        <Field label="Department">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={selectedDepartment?.label || departments?.find(d => d.value === Number(selectedDepartmentId))?.label || ""}
            readOnly
          />
        </Field>

        <Field label="Designation Type">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={typeLabel}
            readOnly
          />
        </Field>

        <Field label="Designation Code">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={designationData.code || ""}
            readOnly
          />
        </Field>

        <Field label="Designation Name">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={designationData.title || ""}
            readOnly
          />
        </Field>

        <Field label="Grade">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={designationData.grade_name || "—"}
            readOnly
          />
        </Field>

        <Field label="Status">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={designationData.status === "active" ? "Active" : "Inactive"}
            readOnly
          />
        </Field>

        <Field label="Created Date">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={formatDate(designationData.created_at)}
            readOnly
          />
        </Field>

        <Field label="Updated Date">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={formatDate(designationData.updated_at)}
            readOnly
          />
        </Field>
      </div>
    </Dialog>
  );
}

/* ==========================================================================
   ADD/EDIT DESIGNATION DIALOG
   ========================================================================== */

function AddDesignationDialog({
  open,
  onClose,
  onSave,
  selectedDepartment,
  selectedType,
  grades,
  editData,
  isSaving,
  validationErrors,
  designationTypes,
  departments,
  selectedDepartmentId,
}) {
  const [form, setForm] = useState({
    code: "",
    title: "",
    gradeId: "",
    status: "active",
    isNpaApplicable: false,
  });

  useEffect(() => {
    if (open) {
      if (editData) {
        setForm({
          code: editData.code || "",
          title: editData.title || "",
          gradeId: editData.grade_id
            ? editData.grade_id.toString()
            : (grades[0]?.value ?? ""),
          status: editData.status || "active",
          isNpaApplicable: !!editData.is_npa_applicable,
          isTeacher: !!editData.is_teacher,
        });
      } else {
        setForm({
          code: "",
          title: "",
          gradeId: grades[0]?.value ?? "",
          status: "active",
          isNpaApplicable: false,
          isTeacher: false,
        });
      }
    }
  }, [open, grades, editData]);

  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const isEditMode = !!editData;
  const typeLabel = designationTypes?.find(t => t.id === selectedType)?.label || selectedType;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEditMode ? "Edit Designation" : "Add Designation"}
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton
            onClick={async () => {
              await onSave({
                ...form,
                departmentId: selectedDepartment.value,
                designationType: selectedType,
              });
            }}
            disabled={isSaving}
          >
            {isSaving
              ? isEditMode
                ? "Updating..."
                : "Saving..."
              : isEditMode
                ? "Update"
                : "Save"}
          </PrimaryButton>
        </>
      }
    >
      <Field label="Designation Code *">
        <input
          className={inputClasses}
          placeholder="e.g. JE001"
          value={form.code}
          onChange={update("code")}
        />
        {validationErrors.code && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.code}</p>
        )}
      </Field>

      <Field label="Designation Name *">
        <input
          className={inputClasses}
          placeholder="e.g. Junior Engineer"
          value={form.title}
          onChange={update("title")}
        />
        {validationErrors.title && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.title}</p>
        )}
      </Field>

      <Field label="Department">
        <input
          className={`${inputClasses} bg-gray-100`}
          value={selectedDepartment?.label || departments?.find(d => d.value === Number(selectedDepartmentId))?.label || ""}
          readOnly
        />
      </Field>

      <Field label="Designation Type">
        <input
          className={`${inputClasses} bg-gray-100`}
          value={typeLabel}
          readOnly
        />
      </Field>

      <Field label="Grade *">
        <select
          className={inputClasses}
          value={form.gradeId}
          onChange={update("gradeId")}
        >
          {grades.map((grade) => (
            <option key={grade.value} value={grade.value}>
              {grade.label}
            </option>
          ))}
        </select>
        {validationErrors.gradeId && (
          <p className="text-xs text-red-600 mt-1">
            {validationErrors.gradeId}
          </p>
        )}
      </Field>

      <Field label="Status">
        <select
          className={inputClasses}
          value={form.status}
          onChange={update("status")}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {validationErrors.status && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.status}</p>
        )}
      </Field>

      <Field label="NPA (Non-Practising Allowance)">
        <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-700 mt-1 select-none">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
            checked={form.isNpaApplicable}
            onChange={(e) => setForm((prev) => ({ ...prev, isNpaApplicable: e.target.checked }))}
          />
          <span className="font-medium">Applicable for NPA (35% of Basic Pay)</span>
        </label>
      </Field>

      <Field label="Teacher Classification">
        <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-700 mt-1 select-none">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
            checked={form.isTeacher}
            onChange={(e) => setForm((prev) => ({ ...prev, isTeacher: e.target.checked }))}
          />
          <span className="font-medium">Teacher Designation (Exempt from TPT in May)</span>
        </label>
      </Field>
    </Dialog>
  );
}

/* ==========================================================================
   VIEW SCALE DIALOG
   ========================================================================== */

function ViewScaleDialog({ open, onClose, scaleData }) {
  if (!scaleData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="View Scale"
      footer={<SecondaryButton onClick={onClose}>Close</SecondaryButton>}
    >
      <div className="space-y-4">
        <Field label="Scale Code">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={scaleData.code || ""}
            readOnly
          />
        </Field>

        <Field label="Scale Name">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={scaleData.name || ""}
            readOnly
          />
        </Field>

        <Field label="Pay Level">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={scaleData.pay_level || ""}
            readOnly
          />
        </Field>

        <Field label="Minimum Basic">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={scaleData.min_basic || ""}
            readOnly
          />
        </Field>

        <Field label="Maximum Basic">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={scaleData.max_basic || ""}
            readOnly
          />
        </Field>

        <Field label="Description">
          <textarea
            rows={3}
            className={`${inputClasses} bg-gray-100`}
            value={scaleData.description || ""}
            readOnly
          />
        </Field>

        <Field label="Status">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={scaleData.status === "active" ? "Active" : "Inactive"}
            readOnly
          />
        </Field>

        <Field label="Created Date">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={formatDate(scaleData.created_at)}
            readOnly
          />
        </Field>

        <Field label="Updated Date">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={formatDate(scaleData.updated_at)}
            readOnly
          />
        </Field>
      </div>
    </Dialog>
  );
}

/* ==========================================================================
   ADD SCALE DIALOG
   ========================================================================== */

function AddScaleDialog({
  open,
  onClose,
  onSave,
  editData,
  isSaving,
  validationErrors,
}) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    level: "",
    minBasic: "",
    maxBasic: "",
    description: "",
    status: "active",
  });

  useEffect(() => {
    if (open) {
      if (editData) {
        setForm({
          code: editData.code || "",
          name: editData.name || "",
          level: editData.pay_level?.toString() || "",
          minBasic: editData.min_basic?.toString() || "",
          maxBasic: editData.max_basic?.toString() || "",
          description: editData.description || "",
          status: editData.status || "active",
        });
      } else {
        setForm({
          code: "",
          name: "",
          level: "",
          minBasic: "",
          maxBasic: "",
          description: "",
          status: "active",
        });
      }
    }
  }, [open, editData]);

  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const isEditMode = !!editData;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEditMode ? "Edit Scale" : "Add Scale"}
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton
            onClick={async () => {
              await onSave(form);
            }}
            disabled={isSaving}
          >
            {isSaving
              ? isEditMode
                ? "Updating..."
                : "Saving..."
              : isEditMode
                ? "Update"
                : "Save"}
          </PrimaryButton>
        </>
      }
    >
      <Field label="Scale Code">
        <input
          type="text"
          className={inputClasses}
          placeholder="e.g. S14"
          value={form.code}
          onChange={update("code")}
        />
        {validationErrors.code && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.code}</p>
        )}
      </Field>
      <Field label="Scale Name">
        <input
          type="text"
          className={inputClasses}
          placeholder="e.g. Level 14"
          value={form.name}
          onChange={update("name")}
        />
        {validationErrors.name && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.name}</p>
        )}
      </Field>
      <Field label="Pay Level">
        <input
          type="number"
          className={inputClasses}
          placeholder="e.g. 14"
          value={form.level}
          onChange={update("level")}
        />
        {validationErrors.level && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.level}</p>
        )}
      </Field>
      <Field label="Minimum Basic">
        <input
          type="number"
          className={inputClasses}
          placeholder="e.g. 15000"
          value={form.minBasic}
          onChange={update("minBasic")}
        />
        {validationErrors.minBasic && (
          <p className="text-xs text-red-600 mt-1">
            {validationErrors.minBasic}
          </p>
        )}
      </Field>
      <Field label="Maximum Basic">
        <input
          type="number"
          className={inputClasses}
          placeholder="e.g. 50000"
          value={form.maxBasic}
          onChange={update("maxBasic")}
        />
        {validationErrors.maxBasic && (
          <p className="text-xs text-red-600 mt-1">
            {validationErrors.maxBasic}
          </p>
        )}
      </Field>
      <Field label="Description">
        <textarea
          rows={3}
          className={inputClasses}
          placeholder="Optional notes about this scale"
          value={form.description}
          onChange={update("description")}
        />
      </Field>
      <Field label="Status">
        <select
          className={inputClasses}
          value={form.status}
          onChange={update("status")}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {validationErrors.status && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.status}</p>
        )}
      </Field>
    </Dialog>
  );
}

/* ==========================================================================
   DELETE CONFIRMATION DIALOG (reusable)
   ========================================================================== */

function DeleteConfirmDialog({ open, onClose, onConfirm, itemLabel }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Delete Confirmation"
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <button
            type="button"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-red-700 active:bg-red-800 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
        </>
      }
    >
      <p className="text-sm text-gray-600">
        Are you sure you want to delete{" "}
        <span className="font-medium text-gray-900">{itemLabel}</span>? This
        action cannot be undone.
      </p>
    </Dialog>
  );
}

/* ==========================================================================
   VIEW MAPPING DIALOG
   ========================================================================== */

function ViewMappingDialog({
  open,
  onClose,
  mappingData,
  selectedDepartment,
  selectedType,
  designationTypes,
  departments,
  selectedDepartmentId,
}) {
  if (!mappingData) return null;

  const typeLabel = designationTypes?.find(t => t.id === selectedType)?.label || selectedType;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="View Mapping"
      footer={<SecondaryButton onClick={onClose}>Close</SecondaryButton>}
    >
      <div className="space-y-4">
        <Field label="Department">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={selectedDepartment?.label || departments?.find(d => d.value === Number(selectedDepartmentId))?.label || ""}
            readOnly
          />
        </Field>

        <Field label="Designation Type">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={typeLabel}
            readOnly
          />
        </Field>

        <Field label="Designation Code">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={mappingData.designation_code || ""}
            readOnly
          />
        </Field>

        <Field label="Designation Name">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={mappingData.designation_name || ""}
            readOnly
          />
        </Field>

        <Field label="Grade">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={mappingData.grade_name || "—"}
            readOnly
          />
        </Field>

        <Field label="Scale Code">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={mappingData.scale_code || ""}
            readOnly
          />
        </Field>

        <Field label="Scale Name">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={mappingData.scale_name || ""}
            readOnly
          />
        </Field>

        <Field label="Effective From">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={formatDate(mappingData.effective_from)}
            readOnly
          />
        </Field>

        <Field label="Effective To">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={formatDate(mappingData.effective_to)}
            readOnly
          />
        </Field>

        <Field label="Status">
          <input
            className={`${inputClasses} bg-gray-100`}
            value={mappingData.status === "active" ? "Active" : "Inactive"}
            readOnly
          />
        </Field>
      </div>
    </Dialog>
  );
}

/* ==========================================================================
   ADD/EDIT MAPPING DIALOG
   ========================================================================== */

function AddMappingDialog({
  open,
  onClose,
  onSave,
  selectedDepartment,
  selectedType,
  designations,
  scales,
  editData,
  isSaving,
  validationErrors,
  designationTypes,
  departments,
  selectedDepartmentId,
}) {
  const [form, setForm] = useState({
    designationId: "",
    scaleId: "",
    effectiveFrom: "",
    effectiveTo: "",
    status: "active",
  });

  useEffect(() => {
    if (open) {
      if (editData) {
        setForm({
          designationId: editData.designation_id?.toString() || "",
          scaleId: editData.scale_id?.toString() || "",
          effectiveFrom: editData.effective_from || "",
          effectiveTo: editData.effective_to || "",
          status: editData.status || "active",
        });
      } else {
        setForm({
          designationId: designations[0]?.id?.toString() || "",
          scaleId: scales[0]?.id?.toString() || "",
          effectiveFrom: "",
          effectiveTo: "",
          status: "active",
        });
      }
    }
  }, [open, designations, scales, editData]);

  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const isEditMode = !!editData;
  const typeLabel = designationTypes?.find(t => t.id === selectedType)?.label || selectedType;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEditMode ? "Edit Mapping" : "Add Mapping"}
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton
            onClick={async () => {
              await onSave(form);
            }}
            disabled={isSaving}
          >
            {isSaving
              ? isEditMode
                ? "Updating..."
                : "Saving..."
              : isEditMode
                ? "Update"
                : "Save"}
          </PrimaryButton>
        </>
      }
    >
      <Field label="Department">
        <input
          className={`${inputClasses} bg-gray-100`}
          value={selectedDepartment?.label || departments?.find(d => d.value === Number(selectedDepartmentId))?.label || ""}
          readOnly
        />
      </Field>

      <Field label="Designation Type">
        <input
          className={`${inputClasses} bg-gray-100`}
          value={typeLabel}
          readOnly
        />
      </Field>

      <Field label="Designation *">
        <select
          className={inputClasses}
          value={form.designationId}
          onChange={update("designationId")}
        >
          {designations.map((d) => (
            <option key={d.id} value={d.id}>
              {d.code} - {d.title}
            </option>
          ))}
        </select>
        {validationErrors.designationId && (
          <p className="text-xs text-red-600 mt-1">
            {validationErrors.designationId}
          </p>
        )}
      </Field>

      <Field label="Scale *">
        <select
          className={inputClasses}
          value={form.scaleId}
          onChange={update("scaleId")}
        >
          {scales.filter(s => s.status === 'active' || s.id.toString() === form.scaleId).map((s) => (
            <option key={s.id} value={s.id}>
              {s.code} - {s.name}
            </option>
          ))}
        </select>
        {validationErrors.scaleId && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.scaleId}</p>
        )}
      </Field>

      <Field label="Effective From *">
        <input
          type="date"
          className={inputClasses}
          value={form.effectiveFrom}
          onChange={update("effectiveFrom")}
        />
        {validationErrors.effectiveFrom && (
          <p className="text-xs text-red-600 mt-1">
            {validationErrors.effectiveFrom}
          </p>
        )}
      </Field>

      <Field label="Effective To">
        <input
          type="date"
          className={inputClasses}
          value={form.effectiveTo}
          onChange={update("effectiveTo")}
        />
        {validationErrors.effectiveTo && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.effectiveTo}</p>
        )}
      </Field>

      <Field label="Status">
        <select
          className={inputClasses}
          value={form.status}
          onChange={update("status")}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {validationErrors.status && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.status}</p>
        )}
      </Field>
    </Dialog>
  );
}

/* ==========================================================================
   MAIN COMPONENT
   ========================================================================== */

export default function DepartmentConfiguration() {
  const { masters, loading } = useEmployeeMasters();
  const { hasPermission } = useAuth();
  
  const canViewDesignation = hasPermission("designation:view") || hasPermission("*") || hasPermission("hr.*");
  const canAddDesignation = hasPermission("designation:create") || hasPermission("*") || hasPermission("hr.*");
  const canUpdateDesignation = hasPermission("designation:update") || hasPermission("*") || hasPermission("hr.*");
  const canViewScale = hasPermission("pay_scale:view") || hasPermission("*") || hasPermission("hr.*");
  const canAddScale = hasPermission("pay_scale:create") || hasPermission("*") || hasPermission("hr.*");
  const canUpdateScale = hasPermission("pay_scale:update") || hasPermission("*") || hasPermission("hr.*");

  const departments = masters?.departments || [];
  const designationTypes = [
    { id: "PROMOTIONAL", label: "Promotional" },
    { id: "ISOLATED", label: "Isolated" },
  ];
  console.log("Masters:", masters);
  console.log("Grades:", masters?.grades);
  const [scales, setScales] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [selectedType, setSelectedType] = useState("PROMOTIONAL");
  const [searchTerm, setSearchTerm] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [addDesignationOpen, setAddDesignationOpen] = useState(false);
  const [addScaleOpen, setAddScaleOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { kind: 'designation'|'scale', id, label }
  const [viewDesignationOpen, setViewDesignationOpen] = useState(false);
  const [viewDesignationData, setViewDesignationData] = useState(null);
  const [editDesignationData, setEditDesignationData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [viewScaleOpen, setViewScaleOpen] = useState(false);
  const [viewScaleData, setViewScaleData] = useState(null);
  const [editScaleData, setEditScaleData] = useState(null);
  const [scaleValidationErrors, setScaleValidationErrors] = useState({});

  const [mappings, setMappings] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingsError, setMappingsError] = useState(null);

  const [addMappingOpen, setAddMappingOpen] = useState(false);
  const [editMappingTarget, setEditMappingTarget] = useState(null);
  const [viewMappingTarget, setViewMappingTarget] = useState(null);

  const [mappingSubmitting, setMappingSubmitting] = useState(false);
  const [mappingSubmitError, setMappingSubmitError] = useState(null);
  const [mappingStatusUpdatingId, setMappingStatusUpdatingId] = useState(null);

  const [departmentHead, setDepartmentHead] = useState(null);
  const [departmentHeadLoading, setDepartmentHeadLoading] = useState(false);
  const [addDepartmentHeadOpen, setAddDepartmentHeadOpen] = useState(false);
  const [deptHeadErrors, setDeptHeadErrors] = useState({});

  const APP_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    console.log("Departments", departments);
  }, [departments]);

  async function refreshScales() {
    try {
      const token = localStorage.getItem("access_token");

      const res = await axios.get(`${APP_URL}/v1/designations/scales`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setScales(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load scales", err);
      setScales([]);
    }
  }

  useEffect(() => {
    refreshScales();
  }, []);

  // Simulate refresh-on-change (backend call point later)
  useEffect(() => {
    if (!selectedDepartmentId) return;

    const fetchDesignations = async () => {
      try {
        setIsLoading(true);

        const token = localStorage.getItem("access_token");

        const res = await axios.get(`${APP_URL}/v1/designations/designation`, {
          params: {
            department_id: selectedDepartmentId,
            designation_type: selectedType.toUpperCase(),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setDesignations(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load designations", err);
        setDesignations([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDesignations();
  }, [selectedDepartmentId, selectedType]);

  const refreshDepartmentHead = async () => {
    if (!selectedDepartmentId) return;
    try {
      setDepartmentHeadLoading(true);
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${APP_URL}/v1/department-heads/department/${selectedDepartmentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDepartmentHead(res.data || null);
    } catch (err) {
      console.error("Failed to load department head", err);
      setDepartmentHead(null);
    } finally {
      setDepartmentHeadLoading(false);
    }
  };

  useEffect(() => {
    refreshDepartmentHead();
  }, [selectedDepartmentId]);

  useEffect(() => {
    if (departments.length > 0 && !selectedDepartmentId) {
      setSelectedDepartmentId(departments[0].value);
    }
  }, [departments, selectedDepartmentId]);

  const scaleMap = useMemo(() => {
    const map = {};
    scales.forEach((s) => (map[s.id] = s));
    return map;
  }, [scales]);

  const filteredDesignations = useMemo(() => {
    return designations.filter((d) => {
      if (!searchTerm.trim()) return true;

      const search = searchTerm.toLowerCase();

      return (
        d.title?.toLowerCase().includes(search) ||
        d.code?.toLowerCase().includes(search) ||
        d.grade_name?.toLowerCase().includes(search) ||
        d.status?.toLowerCase().includes(search)
      );
    });
  }, [designations, searchTerm]);

  const selectedDepartment = useMemo(() => {
    return departments.find(
      (d) => d.value === Number(selectedDepartmentId),
    );
  }, [departments, selectedDepartmentId]);

  const refreshDesignations = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${APP_URL}/v1/designations/designation`, {
        params: {
          department_id: selectedDepartmentId,
          designation_type: selectedType.toUpperCase(),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDesignations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to refresh designations", err);
    }
  };

  const handleViewDesignation = async (id) => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${APP_URL}/v1/designations/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setViewDesignationData(res.data);
      setViewDesignationOpen(true);
    } catch (err) {
      console.error("Failed to fetch designation", err);
      alert("Failed to load designation details.");
    }
  };

  const handleEditDesignation = async (id) => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${APP_URL}/v1/designations/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEditDesignationData(res.data);
      setValidationErrors({});
      setAddDesignationOpen(true);
    } catch (err) {
      console.error("Failed to fetch designation", err);
      alert("Failed to load designation details.");
    }
  };

  const handleAddDesignation = async (form) => {
    // Validation
    const errors = {};
    if (!form.code.trim()) errors.code = "Designation Code is required";
    if (!form.title.trim()) errors.title = "Designation Name is required";
    if (!form.gradeId) errors.gradeId = "Grade is required";
    if (!form.status) errors.status = "Status is required";

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    setIsSaving(true);
    const isEditMode = !!editDesignationData;

    try {
      const token = localStorage.getItem("access_token");

      const payload = {
        code: form.code.trim(),
        title: form.title.trim(),
        grade_id: Number(form.gradeId),
        status: form.status,
        is_npa_applicable: !!form.isNpaApplicable,
        is_teacher: !!form.isTeacher,
      };

      if (isEditMode) {
        // Update existing designation
        await axios.put(
          `${APP_URL}/v1/designations/${editDesignationData.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } else {
        // Create new designation
        payload.department_id = Number(form.departmentId);
        payload.designation_type = form.designationType.toUpperCase();
        await axios.post(`${APP_URL}/v1/designations`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      // Refresh the designation list
      await refreshDesignations();

      setAddDesignationOpen(false);
      setEditDesignationData(null);
      alert(
        isEditMode
          ? "Designation updated successfully."
          : "Designation created successfully.",
      );
    } catch (err) {
      console.error(err);
      const errorMessage =
        err?.response?.data?.detail ||
        (isEditMode
          ? "Failed to update designation."
          : "Failed to create designation.");
      alert(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddScale = async (form) => {
    // Validation
    const errors = {};
    if (!form.code.trim()) errors.code = "Scale Code is required";
    if (!form.name.trim()) errors.name = "Scale Name is required";
    if (!form.level) errors.level = "Pay Level is required";
    if (!form.minBasic) errors.minBasic = "Minimum Basic is required";
    if (!form.maxBasic) errors.maxBasic = "Maximum Basic is required";
    if (!form.status) errors.status = "Status is required";

    const minBasic = Number(form.minBasic);
    const maxBasic = Number(form.maxBasic);
    const payLevel = Number(form.level);

    if (minBasic > maxBasic)
      errors.maxBasic =
        "Maximum Basic must be greater than or equal to Minimum Basic";
    if (payLevel <= 0) errors.level = "Pay Level must be greater than 0";

    if (Object.keys(errors).length > 0) {
      setScaleValidationErrors(errors);
      return;
    }

    setScaleValidationErrors({});
    setIsSaving(true);
    const isEditMode = !!editScaleData;

    try {
      const token = localStorage.getItem("access_token");

      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        pay_level: payLevel,
        min_basic: minBasic,
        max_basic: maxBasic,
        description: form.description,
        status: form.status,
      };

      if (isEditMode) {
        // Update existing scale
        await axios.put(
          `${APP_URL}/v1/designations/scales/${editScaleData.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } else {
        // Create new scale
        await axios.post(`${APP_URL}/v1/designations/scales`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      await refreshScales();

      setAddScaleOpen(false);
      setEditScaleData(null);
      alert(
        isEditMode
          ? "Scale updated successfully."
          : "Scale created successfully.",
      );
    } catch (err) {
      console.error(err);
      const errorMessage =
        err?.response?.data?.detail ||
        (isEditMode ? "Failed to update scale." : "Failed to create scale.");
      alert(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewScale = async (id) => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${APP_URL}/v1/designations/scales/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setViewScaleData(res.data);
      setViewScaleOpen(true);
    } catch (err) {
      console.error("Failed to fetch scale", err);
      alert("Failed to load scale details.");
    }
  };

  const handleEditScale = async (id) => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${APP_URL}/v1/designations/scales/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEditScaleData(res.data);
      setScaleValidationErrors({});
      setAddScaleOpen(true);
    } catch (err) {
      console.error("Failed to fetch scale", err);
      alert("Failed to load scale details.");
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;

    if (deleteTarget.kind === "designation") {
      try {
        const token = localStorage.getItem("access_token");
        await axios.delete(`${APP_URL}/v1/designations/${deleteTarget.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        await refreshDesignations();
        alert("Designation deleted successfully.");
      } catch (err) {
        console.error(err);
        const errorMessage =
          err?.response?.data?.detail || "Failed to delete designation.";
        alert(errorMessage);
      }
    }
    setDeleteTarget(null);
  };

  // ==========================================================================
  // MAPPING API HANDLERS
  // ==========================================================================

  const refreshMappings = async () => {
    if (!selectedDepartmentId) return;

    try {
      setMappingsLoading(true);
      setMappingsError(null);

      const token = localStorage.getItem("access_token");

      const res = await axios.get(`${APP_URL}/v1/designation-scale-mappings`, {
        params: {
          department_id: selectedDepartmentId,
          designation_type: selectedType.toUpperCase(),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setMappings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load mappings", err);
      setMappingsError("Failed to load mappings");
      setMappings([]);
    } finally {
      setMappingsLoading(false);
    }
  };

  // Refresh mappings when department or type changes
  useEffect(() => {
    refreshMappings();
  }, [selectedDepartmentId, selectedType]);

  const handleViewMapping = async (id) => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${APP_URL}/v1/designation-scale-mappings/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setViewMappingTarget(res.data);
    } catch (err) {
      console.error("Failed to fetch mapping", err);
      alert("Failed to load mapping details.");
    }
  };

  const handleEditMapping = async (id) => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${APP_URL}/v1/designation-scale-mappings/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEditMappingTarget(res.data);
      setMappingSubmitError(null);
      setAddMappingOpen(true);
    } catch (err) {
      console.error("Failed to fetch mapping", err);
      alert("Failed to load mapping details.");
    }
  };

  const handleAddMapping = async (form) => {
    // Validation
    const errors = {};
    if (!form.designationId) errors.designationId = "Designation is required";
    if (!form.scaleId) errors.scaleId = "Scale is required";
    if (!form.effectiveFrom) errors.effectiveFrom = "Effective From is required";
    if (!form.status) errors.status = "Status is required";

    if (form.effectiveTo && form.effectiveFrom && form.effectiveTo < form.effectiveFrom) {
      errors.effectiveTo = "Effective To must be after Effective From";
    }

    if (Object.keys(errors).length > 0) {
      setMappingSubmitError(errors);
      return;
    }

    setMappingSubmitError(null);
    setMappingSubmitting(true);
    const isEditMode = !!editMappingTarget;

    try {
      const token = localStorage.getItem("access_token");

      const payload = {
        designation_id: Number(form.designationId),
        scale_id: Number(form.scaleId),
        effective_from: form.effectiveFrom,
        effective_to: form.effectiveTo || null,
        status: form.status,
      };

      if (isEditMode) {
        await axios.put(
          `${APP_URL}/v1/designation-scale-mappings/${editMappingTarget.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } else {
        await axios.post(`${APP_URL}/v1/designation-scale-mappings`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      await refreshMappings();
      setAddMappingOpen(false);
      setEditMappingTarget(null);
      alert(
        isEditMode
          ? "Mapping updated successfully."
          : "Mapping created successfully.",
      );
    } catch (err) {
      console.error(err);
      const errorMessage =
        err?.response?.data?.detail ||
        (isEditMode ? "Failed to update mapping." : "Failed to create mapping.");
      alert(errorMessage);
    } finally {
      setMappingSubmitting(false);
    }
  };

  const handleUpdateMappingStatus = async (id, newStatus) => {
    try {
      setMappingStatusUpdatingId(id);
      const token = localStorage.getItem("access_token");

      await axios.patch(
        `${APP_URL}/v1/designation-scale-mappings/${id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      await refreshMappings();
      alert("Status updated successfully.");
    } catch (err) {
      console.error(err);
      const errorMessage =
        err?.response?.data?.detail || "Failed to update status.";
      alert(errorMessage);
    } finally {
      setMappingStatusUpdatingId(null);
    }
  };

  // const handleSaveDepartmentHead = async (form) => {
  //   const errors = {};
  //   if (!form.headCode.trim()) errors.headCode = "Head Code is required";
  //   if (!form.headName.trim()) errors.headName = "Head Name is required";
  //   if (!form.effectiveFrom) errors.effectiveFrom = "Effective From date is required";
  //   if (form.effectiveTo && form.effectiveFrom && form.effectiveTo < form.effectiveFrom) {
  //     errors.effectiveTo = "Effective To must be after Effective From";
  //   }

  //   if (Object.keys(errors).length > 0) {
  //     setDeptHeadErrors(errors);
  //     return;
  //   }

  //   setDeptHeadErrors({});
  //   setIsSaving(true);

  //   try {
  //     const token = localStorage.getItem("access_token");
  //     const payload = {
  //       department_id: Number(form.departmentId),
  //       head_code: form.headCode.trim(),
  //       head_name: form.headName.trim(),
  //       employee_code: form.employeeCode.trim() || null,
  //       effective_from: form.effectiveFrom,
  //       effective_to: form.effectiveTo || null,
  //       status: form.status
  //     };

  //     await axios.post(`${APP_URL}/v1/department-heads`, payload, {
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });

  //     await refreshDepartmentHead();
  //     setAddDepartmentHeadOpen(false);
  //     alert("Department Head assigned successfully.");
  //   } catch (err) {
  //     console.error(err);
  //     alert(err?.response?.data?.detail || "Failed to assign Department Head.");
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Page Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm shrink-0">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                Department Configuration
              </h1>
              <p className="text-sm text-gray-500">
                Configure department-wise designations and pay scales.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* SECTION 1 — Department Selection */}
        <Card className="p-5 sm:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex-1">
              <Field label="Department">
                <div className="max-w-md">
                  <select
                    className={inputClasses}
                    value={selectedDepartmentId}
                    onChange={(e) => setSelectedDepartmentId(e.target.value)}
                  >
                    {departments.map((dept) => (
                      <option key={dept.value} value={dept.value}>
                        {dept.label}
                      </option>
                    ))}
                  </select>
                </div>
              </Field>
            </div>
            {/* <div className="shrink-0 flex flex-col items-start md:items-end gap-2 pt-2 md:pt-0">
              <PrimaryButton onClick={() => setAddDepartmentHeadOpen(true)}>
                <ShieldCheck className="h-4 w-4" />
                Department Head
              </PrimaryButton>
            </div> */}
          </div>

          <div className="mt-6 pt-5 border-t border-gray-200">
            <span className="block text-sm font-medium text-gray-700 mb-2">
              Department Head Info
            </span>

            {departmentHeadLoading ? (
              <p className="text-sm text-gray-400">
                Loading Department Head info...
              </p>
            ) : departmentHead?.heads?.length > 0 ? (
              <div className="max-w-3xl">
                {/* Department Information */}
                <div className="mb-3">
                  <span className="text-sm font-semibold text-gray-900">
                    {departmentHead.department_name}
                  </span>

                  <span className="ml-2 text-xs text-gray-500">
                    ({departmentHead.department_code})
                  </span>
                </div>

                {/* Department Heads */}
                <div className="space-y-3">
                  {departmentHead.heads.map((head) => (
                    <div
                      key={head.id}
                      className="bg-blue-50/50 rounded-xl p-4 border border-blue-100 grid grid-cols-1 sm:grid-cols-4 gap-4"
                    >
                      {/* Head Code */}
                      <div>
                        <span className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                          Head Code
                        </span>

                        <span className="text-sm font-semibold text-gray-900 mt-1 block">
                          {head.head_code}
                        </span>
                      </div>

                      {/* Effective From */}
                      <div>
                        <span className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                          Effective From
                        </span>

                        <span className="text-sm font-semibold text-gray-900 mt-1 block">
                          {formatDate(head.effective_from)}
                        </span>
                      </div>

                      {/* Effective To */}
                      <div>
                        <span className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                          Effective To
                        </span>

                        <span className="text-sm font-semibold text-gray-900 mt-1 block">
                          {formatDate(head.effective_to)}
                        </span>
                      </div>

                      {/* Status */}
                      <div>
                        <span className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                          Status
                        </span>

                        <span className="mt-1 block">
                          <StatusBadge status={head.status} />
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                No Department Heads Configured
              </p>
            )}
          </div>

          {/* SECTION 2 — Designation Type */}
          <div className="mt-5">
            <span className="block text-sm font-medium text-gray-700 mb-1.5">
              Designation Type
            </span>
            <div className="inline-flex rounded-xl border border-gray-300 bg-gray-100 p-1 gap-1">
              {designationTypes.map((t) => {
                const isSelected = selectedType === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedType(t.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isSelected
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-white"
                      }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </Card>

        {/* SECTION 3 & 4 — Designations + Scale Master */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Designations (large card) */}
          <Card className="lg:col-span-2 overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Designations
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {selectedDepartment?.label} ·{" "}
                  {designationTypes.find((t) => t.id === selectedType)?.label}
                </p>
              </div>
              {canAddDesignation && (
                <PrimaryButton onClick={() => setAddDesignationOpen(true)}>
                  <Plus className="h-4 w-4" />
                  Add Designation
                </PrimaryButton>
              )}
            </div>

            <div className="px-5 sm:px-6 pt-4">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search designations..."
                  className={`${inputClasses} pl-9`}
                />
              </div>
            </div>

            <div className="overflow-x-auto mt-3">
              {!canViewDesignation ? (
                <div className="py-14 text-center text-sm text-gray-400">
                  Designations view permission required.
                </div>
              ) : isLoading ? (
                <div className="py-14 text-center text-sm text-gray-400">
                  Loading designations...
                </div>
              ) : filteredDesignations.length === 0 ? (
                <EmptyState
                  title={`No ${selectedType === "promotional" ? "Promotional" : "Isolated"} Designations`}
                  subtitle={`for ${selectedDepartment?.label || "this department"}\nClick "Add Designation" to create one.`}
                />
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
                      <th className="px-5 sm:px-6 py-3">Code</th>
                      <th className="px-5 sm:px-6 py-3">Designation</th>
                      <th className="px-3 py-3">Grade</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDesignations.map((d) => (
                      <tr
                        key={d.id}
                        className="border-b border-gray-50 last:border-0 hover:bg-blue-50/40 transition-colors"
                      >
                        <td className="px-5 sm:px-6 py-3.5 font-medium text-gray-900">
                          {d.code}
                        </td>
                        <td className="px-5 sm:px-6 py-3.5">
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            <span>{d.title}</span>
                            {d.is_npa_applicable && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-800 border border-purple-200">
                                NPA
                              </span>
                            )}
                            {d.is_teacher && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">
                                Teacher
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3.5 text-gray-600">
                          {d?.grade_name || "-"}
                        </td>
                        <td className="px-3 py-3.5">
                          <StatusBadge status={d.status} />
                        </td>
                        <td className="px-3 py-3.5">
                          <div className="flex items-center justify-end gap-1">
                            <IconButton
                              label="View designation"
                              onClick={() => handleViewDesignation(d.id)}
                            >
                              <Eye className="h-4 w-4" />
                            </IconButton>
                            {canUpdateDesignation && (
                              <IconButton
                                label="Edit designation"
                                onClick={() => handleEditDesignation(d.id)}
                              >
                                <Pencil className="h-4 w-4" />
                              </IconButton>
                            )}
                            {/* <IconButton
                              label="Delete designation"
                              tone="danger"
                              onClick={() =>
                                setDeleteTarget({
                                  kind: "designation",
                                  id: d.id,
                                  label: d.title,
                                })
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </IconButton> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>

          {/* Scale Master */}
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">
                Scale Master
              </h2>
              {canAddScale && (
                <PrimaryButton onClick={() => setAddScaleOpen(true)}>
                  <Plus className="h-4 w-4" />
                  Add Scale
                </PrimaryButton>
              )}
            </div>

            <div className="overflow-x-auto">
              {!canViewScale ? (
                <div className="py-14 text-center text-sm text-gray-400">
                  Pay scales view permission required.
                </div>
              ) : scales.length === 0 ? (
                <EmptyState
                  title="No scales configured"
                  subtitle="Add a scale to get started."
                />
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
                      <th className="px-5 sm:px-6 py-3">Scale Code</th>
                      <th className="px-3 py-3">Scale Name</th>
                      <th className="px-3 py-3">Pay Level</th>
                      <th className="px-3 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scales.map((s) => (
                      <tr
                        key={s.id}
                        className="border-b border-gray-50 last:border-0 hover:bg-blue-50/40 transition-colors"
                      >
                        <td className="px-5 sm:px-6 py-3.5 font-medium text-gray-900">
                          {s.code}
                        </td>
                        <td className="px-3 py-3.5 text-gray-600">{s.name}</td>
                        <td className="px-3 py-3.5 text-gray-600">
                          {s.pay_level}
                        </td>
                        <td className="px-3 py-3.5">
                          <div className="flex items-center justify-end gap-1">
                            <IconButton
                              label="View scale"
                              onClick={() => handleViewScale(s.id)}
                            >
                              <Eye className="h-4 w-4" />
                            </IconButton>
                            {canUpdateScale && (
                              <IconButton
                                label="Edit scale"
                                onClick={() => handleEditScale(s.id)}
                              >
                                <Pencil className="h-4 w-4" />
                              </IconButton>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </div>

        {/* SECTION 5 — Designation ↔ Scale Mapping */}
        {/** Designation ↔ Scale Mapping block commented out per request **/}
        {/*
        <Card className="overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-100">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Designation ↔ Scale Mapping
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {selectedDepartment?.label} ·{" "}
                {designationTypes.find((t) => t.id === selectedType)?.label}
              </p>
            </div>
            <PrimaryButton onClick={() => setAddMappingOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Mapping
            </PrimaryButton>
          </div>

          <div className="overflow-x-auto">
            {mappingsLoading ? (
              <div className="py-14 text-center text-sm text-gray-400">
                Loading mappings...
              </div>
            ) : mappings.length === 0 ? (
              <EmptyState
                title="No mappings configured"
                subtitle="Add a mapping to link designations with scales."
              />
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
                    <th className="px-5 sm:px-6 py-3">Designation Code</th>
                    <th className="px-5 sm:px-6 py-3">Designation Name</th>
                    <th className="px-3 py-3">Grade</th>
                    <th className="px-3 py-3">Scale Code</th>
                    <th className="px-3 py-3">Scale Name</th>
                    <th className="px-3 py-3">Effective From</th>
                    <th className="px-3 py-3">Effective To</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m) => (
                    <tr
                      key={m.id}
                      className="border-b border-gray-50 last:border-0 hover:bg-blue-50/40 transition-colors"
                    >
                      <td className="px-5 sm:px-6 py-3.5 font-medium text-gray-900">
                        {m.designation_code}
                      </td>
                      <td className="px-5 sm:px-6 py-3.5">
                        <div className="font-medium text-gray-900">
                          {m.designation_name}
                        </div>
                      </td>
                      <td className="px-3 py-3.5 text-gray-600">
                        {m.grade_name || "-"}
                      </td>
                      <td className="px-3 py-3.5 text-gray-600">
                        {m.scale_code}
                      </td>
                      <td className="px-3 py-3.5 text-gray-600">
                        {m.scale_name}
                      </td>
                      <td className="px-3 py-3.5 text-gray-600">
                        {formatDate(m.effective_from, '-')}
                      </td>
                      <td className="px-3 py-3.5 text-gray-600">
                        {formatDate(m.effective_to, '-')}
                      </td>
                      <td className="px-3 py-3.5">
                        <StatusBadge status={m.status} />
                      </td>
                      <td className="px-3 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <IconButton
                            label="View mapping"
                            onClick={() => handleViewMapping(m.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </IconButton>
                          <IconButton
                            label="Edit mapping"
                            onClick={() => handleEditMapping(m.id)}
                          >
                            <Pencil className="h-4 w-4" />
                          </IconButton>
                          {mappingStatusUpdatingId === m.id ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                          ) : (
                            <IconButton
                              label="Toggle status"
                              onClick={() =>
                                handleUpdateMappingStatus(
                                  m.id,
                                  m.status === "active" ? "inactive" : "active"
                                )
                              }
                            >
                              {m.status === "active" ? (
                                <Lock className="h-4 w-4" />
                              ) : (
                                <ShieldCheck className="h-4 w-4" />
                              )}
                            </IconButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
        */}
      </div>

      {/* Dialogs */}
      <AddDesignationDialog
        open={addDesignationOpen}
        onClose={() => {
          setAddDesignationOpen(false);
          setEditDesignationData(null);
          setValidationErrors({});
        }}
        onSave={handleAddDesignation}
        selectedDepartment={selectedDepartment}
        selectedType={selectedType}
        grades={masters?.grades || []}
        editData={editDesignationData}
        isSaving={isSaving}
        validationErrors={validationErrors}
        designationTypes={designationTypes}
        departments={departments}
        selectedDepartmentId={selectedDepartmentId}
      />

      <ViewDesignationDialog
        open={viewDesignationOpen}
        onClose={() => {
          setViewDesignationOpen(false);
          setViewDesignationData(null);
        }}
        designationData={viewDesignationData}
        selectedDepartment={selectedDepartment}
        selectedType={selectedType}
        designationTypes={designationTypes}
        departments={departments}
        selectedDepartmentId={selectedDepartmentId}
      />

      <AddScaleDialog
        open={addScaleOpen}
        onClose={() => {
          setAddScaleOpen(false);
          setEditScaleData(null);
          setScaleValidationErrors({});
        }}
        onSave={handleAddScale}
        editData={editScaleData}
        isSaving={isSaving}
        validationErrors={scaleValidationErrors}
      />

      <ViewScaleDialog
        open={viewScaleOpen}
        onClose={() => {
          setViewScaleOpen(false);
          setViewScaleData(null);
        }}
        scaleData={viewScaleData}
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirmed}
        itemLabel={deleteTarget?.label}
      />

      {/* Mapping Dialogs */}
      <AddMappingDialog
        open={addMappingOpen}
        onClose={() => {
          setAddMappingOpen(false);
          setEditMappingTarget(null);
          setMappingSubmitError(null);
        }}
        onSave={handleAddMapping}
        selectedDepartment={selectedDepartment}
        selectedType={selectedType}
        designations={designations}
        scales={scales}
        editData={editMappingTarget}
        isSaving={mappingSubmitting}
        validationErrors={mappingSubmitError || {}}
        designationTypes={designationTypes}
        departments={departments}
        selectedDepartmentId={selectedDepartmentId}
      />

      <ViewMappingDialog
        open={!!viewMappingTarget}
        onClose={() => {
          setViewMappingTarget(null);
        }}
        mappingData={viewMappingTarget}
        selectedDepartment={selectedDepartment}
        selectedType={selectedType}
        designationTypes={designationTypes}
        departments={departments}
        selectedDepartmentId={selectedDepartmentId}
      />

      {/* <AssignDepartmentHeadDialog
        open={addDepartmentHeadOpen}
        onClose={() => {
          setAddDepartmentHeadOpen(false);
          setDeptHeadErrors({});
        }}
        onSave={handleSaveDepartmentHead}
        selectedDepartment={selectedDepartment}
        editData={departmentHead}
        isSaving={isSaving}
        validationErrors={deptHeadErrors}
      /> */}
    </div>
  );
}

/* ==========================================================================
   ASSIGN DEPARTMENT HEAD DIALOG
//    ========================================================================== */

// function AssignDepartmentHeadDialog({
//   open,
//   onClose,
//   onSave,
//   selectedDepartment,
//   editData,
//   isSaving,
//   validationErrors
// }) {
//   const [form, setForm] = useState({
//     headCode: "",
//     headName: "",
//     employeeCode: "",
//     effectiveFrom: "",
//     effectiveTo: "",
//     status: "active"
//   });

//   useEffect(() => {
//     if (open) {
//       if (editData) {
//         setForm({
//           headCode: editData.head_code || "",
//           headName: editData.head_name || "",
//           employeeCode: editData.employee_code || "",
//           effectiveFrom: editData.effective_from || "",
//           effectiveTo: editData.effective_to || "",
//           status: editData.status || "active"
//         });
//       } else {
//         setForm({
//           headCode: "",
//           headName: "",
//           employeeCode: "",
//           effectiveFrom: "",
//           effectiveTo: "",
//           status: "active"
//         });
//       }
//     }
//   }, [open, editData]);

//   const update = (key) => (e) =>
//     setForm((prev) => ({ ...prev, [key]: e.target.value }));

//   return (
//     <Dialog
//       open={open}
//       onClose={onClose}
//       title="Department Head Assignment"
//       footer={
//         <>
//           <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
//           <PrimaryButton
//             onClick={() => {
//               onSave({
//                 ...form,
//                 departmentId: selectedDepartment.value
//               });
//             }}
//             disabled={isSaving}
//           >
//             {isSaving ? "Saving..." : "Save"}
//           </PrimaryButton>
//         </>
//       }
//     >
//       <Field label="Department (Read Only)">
//         <input
//           className={`${inputClasses} bg-gray-100`}
//           value={selectedDepartment?.label || ""}
//           readOnly
//         />
//       </Field>

//       <Field label="Head Code *">
//         <input
//           className={inputClasses}
//           placeholder="e.g. H001"
//           value={form.headCode}
//           onChange={update("headCode")}
//         />
//         {validationErrors.headCode && (
//           <p className="text-xs text-red-600 mt-1">{validationErrors.headCode}</p>
//         )}
//       </Field>

//       <Field label="Head Name *">
//         <input
//           className={inputClasses}
//           placeholder="e.g. Mr. Omkar Istalkar"
//           value={form.headName}
//           onChange={update("headName")}
//         />
//         {validationErrors.headName && (
//           <p className="text-xs text-red-600 mt-1">{validationErrors.headName}</p>
//         )}
//       </Field>

//       <Field label="Employee Code (Optional)">
//         <input
//           className={inputClasses}
//           placeholder="e.g. EMP00002"
//           value={form.employeeCode}
//           onChange={update("employeeCode")}
//         />
//       </Field>

//       <Field label="Effective From *">
//         <input
//           type="date"
//           className={inputClasses}
//           value={form.effectiveFrom}
//           onChange={update("effectiveFrom")}
//         />
//         {validationErrors.effectiveFrom && (
//           <p className="text-xs text-red-600 mt-1">{validationErrors.effectiveFrom}</p>
//         )}
//       </Field>

//       <Field label="Effective To (Optional)">
//         <input
//           type="date"
//           className={inputClasses}
//           value={form.effectiveTo}
//           onChange={update("effectiveTo")}
//         />
//         {validationErrors.effectiveTo && (
//           <p className="text-xs text-red-600 mt-1">{validationErrors.effectiveTo}</p>
//         )}
//       </Field>

//       <Field label="Status">
//         <select
//           className={inputClasses}
//           value={form.status}
//           onChange={update("status")}
//         >
//           <option value="active">Active</option>
//           <option value="inactive">Inactive</option>
//         </select>
//       </Field>
//     </Dialog>
//   );
// }
